import { api } from "@/lib/api-client";
import type { ApiResponse } from "@/types/api";
import type {
  Tenant,
  UpdateTenantProfileDTO,
  Campus,
  CreateCampusDTO,
  UpdateCampusDTO,
  CampusListParams,
  TenantSubscription,
  UpdateSubscriptionDTO,
  TenantSetting,
  UpsertSettingsDTO,
  SubscriptionHistory,
} from "../types/tenant.types";

// ── Tenant (Organization) ──────────────────────────────────────────────────
// GET  /tenant        — get current tenant / organization profile
// PUT  /tenant        — update mutable profile fields (name, contact, logo, etc.)

export const tenantApi = {
  get: () =>
    api.get<ApiResponse<Tenant>>("/tenant").then((r) => r.data.data),

  update: (data: UpdateTenantProfileDTO) =>
    api.put<ApiResponse<Tenant>>("/tenant", data).then((r) => r.data.data),

  // ── Campuses (Individual Schools) ─────────────────────────────────────────
  // GET    /tenant/campuses              — list all campuses for this tenant
  // POST   /tenant/campuses              — create a new campus/school branch
  // GET    /tenant/campuses/:id          — get a single campus
  // PUT    /tenant/campuses/:id          — update campus details
  // DELETE /tenant/campuses/:id          — deactivate/delete a campus
  // POST   /tenant/campuses/:id/primary  — set as primary campus

  getCampuses: (params?: CampusListParams) =>
    api
      .get<ApiResponse<Campus[]>>("/tenant/campuses", { params })
      .then((r) => r.data),

  getCampus: (id: string) =>
    api.get<ApiResponse<Campus>>(`/tenant/campuses/${id}`).then((r) => r.data.data),

  createCampus: (data: CreateCampusDTO) =>
    api.post<ApiResponse<Campus>>("/tenant/campuses", data).then((r) => r.data.data),

  updateCampus: (id: string, data: UpdateCampusDTO) =>
    api.put<ApiResponse<Campus>>(`/tenant/campuses/${id}`, data).then((r) => r.data.data),

  deleteCampus: (id: string) =>
    api.delete<ApiResponse<void>>(`/tenant/campuses/${id}`).then(() => {}),

  setPrimaryCampus: (id: string) =>
    api.post<ApiResponse<Campus>>(`/tenant/campuses/${id}/primary`).then((r) => r.data.data),

  // ── Subscription ──────────────────────────────────────────────────────────
  // GET  /tenant/subscription
  // PUT  /tenant/subscription   (platform-admin only)
  // GET  /tenant/subscription/history

  getSubscription: () =>
    api.get<ApiResponse<TenantSubscription>>("/tenant/subscription").then((r) => r.data.data),

  updateSubscription: (data: UpdateSubscriptionDTO) =>
    api.put<ApiResponse<TenantSubscription>>("/tenant/subscription", data).then((r) => r.data.data),

  getSubscriptionHistory: (params?: { page?: number; page_size?: number }) =>
    api
      .get<ApiResponse<SubscriptionHistory[]>>("/tenant/subscription/history", { params })
      .then((r) => r.data),

  // ── Settings ──────────────────────────────────────────────────────────────
  // GET  /tenant/settings
  // PUT  /tenant/settings

  getSettings: (params?: { page?: number; page_size?: number }) =>
    api
      .get<ApiResponse<TenantSetting[]>>("/tenant/settings", { params })
      .then((r) => r.data),

  upsertSettings: (data: UpsertSettingsDTO) =>
    api.put<ApiResponse<TenantSetting[]>>("/tenant/settings", data).then((r) => r.data.data),

  getSettingsHistory: (params?: { page?: number; page_size?: number }) =>
    api
      .get<ApiResponse<TenantSettingHistoryEvent[]>>("/tenant/settings/history", { params })
      .then((r) => r.data),

  resetSettings: () =>
    api.post<ApiResponse<{ deleted: number }>>("/tenant/settings/reset").then((r) => r.data.data),

  // ── Self-service (usage, logo, export, deactivation) ───────────────────────

  getUsage: () =>
    api.get<ApiResponse<TenantUsage>>("/tenant/usage").then((r) => r.data.data),

  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post<ApiResponse<{ logo_url: string }>>("/tenant/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data);
  },

  startExport: () =>
    api
      .post<ApiResponse<{ export_id: string; status: string }>>("/tenant/export")
      .then((r) => r.data.data),

  getExport: (id: string) =>
    api
      .get<ApiResponse<{ export_id: string; status: string; download_url: string }>>(
        `/tenant/export/${id}`,
      )
      .then((r) => r.data.data),

  getDeactivationStatus: () =>
    api
      .get<ApiResponse<DeactivationStatus>>("/tenant/deactivation-request")
      .then((r) => r.data.data),

  requestDeactivation: (reason: string) =>
    api.post<ApiResponse<null>>("/tenant/deactivation-request", { reason }).then((r) => r.data),

  cancelDeactivation: () =>
    api.delete<ApiResponse<null>>("/tenant/deactivation-request").then((r) => r.data),
};

export interface TenantUsage {
  students: { used: number; limit: number };
  staff: { used: number };
  users: { used: number };
  storage_bytes: { used: number };
  subscription_tier: string;
}

export interface DeactivationStatus {
  requested: boolean;
  requested_at?: string;
  reason?: string;
}

export interface TenantSettingHistoryEvent {
  id: string;
  actor_name?: string;
  action: string;
  old_values?: unknown;
  new_values?: unknown;
  created_at: string;
}
